import { z } from 'zod';

export const ResourceTriggerComparisonOperator = z.union([
  z.object({ eq: z.unknown() }),
  z.object({ ne: z.unknown() }),
  z.object({ gt: z.unknown() }),
  z.object({ gte: z.unknown() }),
  z.object({ lt: z.unknown() }),
  z.object({ lte: z.unknown() }),
  z.object({ in: z.array(z.unknown()) }),
  z.object({ nin: z.array(z.unknown()) }),
  z.object({ exists: z.boolean() }),
  z.object({ contains: z.string() }),
]);
export type ResourceTriggerComparisonOperator = z.infer<typeof ResourceTriggerComparisonOperator>;

export const ResourceTriggerDiffCriterion = z.record(
  z.union([
    ResourceTriggerComparisonOperator,
    z.object({
      from: z.unknown(),
      to: z.unknown(),
    }),
  ])
);
export type ResourceTriggerDiffCriterion = z.infer<typeof ResourceTriggerDiffCriterion>;

export const ResourceTriggerRelationshipCriterion = z.record(
  z.object({
    where: z.record(ResourceTriggerComparisonOperator),
  })
);
export type ResourceTriggerRelationshipCriterion = z.infer<
  typeof ResourceTriggerRelationshipCriterion
>;

const ResourceTriggerLeafCriteria = z
  .object({
    diff: ResourceTriggerDiffCriterion.optional(),
    relationships: ResourceTriggerRelationshipCriterion.optional(),
  })
  .strict()
  .refine((data) => data.diff ?? data.relationships ?? Object.keys(data).length === 0, {
    message:
      "Criteria must be an empty object {} (match all) or contain at least one of 'diff' or 'relationships'",
  });
type ResourceTriggerLeafCriteriaType = z.infer<typeof ResourceTriggerLeafCriteria>;

export type ResourceTriggerCriteria =
  | { and: ResourceTriggerCriteria[] }
  | { or: ResourceTriggerCriteria[] }
  | { not: ResourceTriggerCriteria }
  | ResourceTriggerLeafCriteriaType;

export const ResourceTriggerCriteria: z.ZodType<ResourceTriggerCriteria> = z.lazy(() =>
  z.union([
    z.object({ and: z.array(ResourceTriggerCriteria) }).strict(),
    z.object({ or: z.array(ResourceTriggerCriteria) }).strict(),
    z.object({ not: ResourceTriggerCriteria }).strict(),
    ResourceTriggerLeafCriteria,
  ])
);
